// src/services/transactionRepository.js - Fully updated for collaborator workspaces
import { auth, db, storage } from "../firebase/config";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  serverTimestamp 
} from "firebase/firestore";
import { ref, getBlob } from "firebase/storage";
import { parseOFXContent } from "../utils/OFXParser";

/**
 * Repositório para interações com Firebase/Firestore
 * Adaptado para suportar contas separadas (pessoal/empresarial), múltiplos perfis,
 * e acesso de colaboradores aos dados do proprietário do workspace
 */

/**
 * Verifica se o usuário atual é um colaborador e retorna informações do workspace
 * @returns {Promise<Object|null>} - Informações do workspace ou null se não for colaborador
 */
const getCollaboratorWorkspaceInfo = async () => {
  try {
    if (!auth.currentUser) return null;
    
    // Obter o ID do perfil ativo
    const activeProfileId = localStorage.getItem('active_business_profile_id');
    if (!activeProfileId) return null;
    
    // NOVO: Obter o UID do proprietário do workspace ativo (se disponível)
    const activeOwnerUid = localStorage.getItem('active_workspace_owner_uid');
    
    console.log(`[TRANSACTION REPO] Checking collaborator access for profile ID: ${activeProfileId}, owner: ${activeOwnerUid || 'unknown'}`);
    
    // Verificar o documento do usuário
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (!userDoc.exists()) return null;
    
    const userData = userDoc.data();
    
    // Verificar se é colaborador
    if (userData.isCollaborator !== true) return null;
    
    // Verificar array de collaboratorWorkspaces
    if (userData.collaboratorWorkspaces && Array.isArray(userData.collaboratorWorkspaces)) {
      console.log(`[TRANSACTION REPO] Found ${userData.collaboratorWorkspaces.length} workspaces, looking for matching workspace`);
      
      // Debug available workspaces
      userData.collaboratorWorkspaces.forEach((w, index) => {
        console.log(`[TRANSACTION REPO] Workspace ${index+1}: id=${w.profileId}, owner=${w.ownerUid}, name=${w.workspaceName || 'Unknown'}`);
      });
      
      // UPDATED: Find workspace based on both profileId and ownerUid if available
      let workspace;
      
      if (activeOwnerUid) {
        // If we have both profileId and ownerUid, use them together for precise matching
        console.log(`[TRANSACTION REPO] Searching for workspace with profileId=${activeProfileId} AND ownerUid=${activeOwnerUid}`);
        workspace = userData.collaboratorWorkspaces.find(
          w => w.profileId === activeProfileId && w.ownerUid === activeOwnerUid
        );
      } else {
        // Fallback to just profileId matching if no ownerUid is available
        workspace = userData.collaboratorWorkspaces.find(
          w => w.profileId === activeProfileId
        );
      }
      
      if (workspace && workspace.ownerUid) {
        console.log(`[TRANSACTION REPO] Found matching workspace: ${workspace.workspaceName}, owner: ${workspace.ownerUid}`);
        
        // Store the owner UID for future reference
        if (!activeOwnerUid) {
          localStorage.setItem('active_workspace_owner_uid', workspace.ownerUid);
          console.log(`[TRANSACTION REPO] Stored owner UID in localStorage: ${workspace.ownerUid}`);
        }
        
        // Verificar se ainda tem acesso ao perfil
        const profileRef = doc(db, 'users', workspace.ownerUid, 'businessProfiles', activeProfileId);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          
          // Verificar se é membro
          if (profileData.members && profileData.members[auth.currentUser.uid]) {
            console.log(`[TRANSACTION REPO] Confirmed access as ${profileData.members[auth.currentUser.uid]}`);
            
            return {
              isCollaborator: true,
              ownerUid: workspace.ownerUid,
              profileId: activeProfileId,
              role: profileData.members[auth.currentUser.uid],
              profileName: profileData.profileName || workspace.workspaceName || 'Workspace'
            };
          } else {
            console.log(`[TRANSACTION REPO] User not found in members list`);
          }
        } else {
          console.log(`[TRANSACTION REPO] Profile document not found`);
        }
      } else {
        console.log(`[TRANSACTION REPO] No matching workspace found with the criteria`);
        
        // If we couldn't find with precise matching, try a more lenient approach
        // This would be used as a last resort if the composite ID approach fails
        if (activeOwnerUid) {
          console.log(`[TRANSACTION REPO] Trying fallback: searching by profileId only`);
          workspace = userData.collaboratorWorkspaces.find(
            w => w.profileId === activeProfileId
          );
          
          if (workspace) {
            console.log(`[TRANSACTION REPO] Found workspace by profileId only: ${workspace.workspaceName}`);
            // Continue with this workspace...
            
            // Update stored owner UID to match the found workspace
            localStorage.setItem('active_workspace_owner_uid', workspace.ownerUid);
            
            // Verify access
            const profileRef = doc(db, 'users', workspace.ownerUid, 'businessProfiles', activeProfileId);
            const profileDoc = await getDoc(profileRef);
            
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              
              if (profileData.members && profileData.members[auth.currentUser.uid]) {
                console.log(`[TRANSACTION REPO] Confirmed access via profileId-only method`);
                
                return {
                  isCollaborator: true,
                  ownerUid: workspace.ownerUid,
                  profileId: activeProfileId,
                  role: profileData.members[auth.currentUser.uid],
                  profileName: profileData.profileName || workspace.workspaceName || 'Workspace'
                };
              }
            }
          }
        }
      }
    }
    
    // Fallback: Verificar convite original (método legado)
    const originalInvitation = userData.originalInvitation || {};
    const invitedBy = userData.invitedBy;
    
    // Se o perfil ativo corresponde ao perfil para o qual foi convidado
    if (activeProfileId === originalInvitation.profileId && invitedBy) {
      console.log(`[TRANSACTION REPO] Using legacy invitation method`);
      
      // Verificar se ainda tem acesso ao perfil
      const profileRef = doc(db, 'users', invitedBy, 'businessProfiles', activeProfileId);
      const profileDoc = await getDoc(profileRef);
      
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        
        // Verificar se é membro
        if (profileData.members && profileData.members[auth.currentUser.uid]) {
          return {
            isCollaborator: true,
            ownerUid: invitedBy,
            profileId: activeProfileId,
            role: profileData.members[auth.currentUser.uid],
            profileName: profileData.profileName || 'Workspace'
          };
        }
      }
    }
    
    // Se chegou até aqui, não encontrou o workspace ativo
    console.log('[TRANSACTION REPO] Collaborator workspace not found in user data');
    return null;
  } catch (error) {
    console.error("Erro ao verificar status de colaborador:", error);
    return null;
  }
};

/**
 * Obtém o subcaminho para a conta ativa atual usando o contexto
 * @returns {Promise<Object>} - Informações do caminho da conta
 */
const getAccountPath = async () => {
  try {
    // Verificar primeiro se é um colaborador
    const collaboratorInfo = await getCollaboratorWorkspaceInfo();
    
    if (collaboratorInfo) {
      console.log(`[TRANSACTION REPO] Using collaborator path: userId=${collaboratorInfo.ownerUid}, subpath=${collaboratorInfo.profileId}`);
      return {
        userId: collaboratorInfo.ownerUid, // Usar o UID do proprietário
        subpath: collaboratorInfo.profileId,
        isCollaborator: true,
        collaboratorInfo
      };
    }
    
    // Se não for colaborador, usar a lógica normal
    const accountType = localStorage.getItem('active_account_type') || 'business';
    
    if (accountType === 'personal') {
      return {
        userId: auth.currentUser.uid,
        subpath: 'personal',
        isCollaborator: false
      };
    } else {
      // Para tipo empresarial, obter o ID do perfil ativo
      const profileId = localStorage.getItem('active_business_profile_id');
      
      // Se for o perfil principal, sempre usar 'business'
      if (!profileId) {
        return {
          userId: auth.currentUser.uid,
          subpath: 'business',
          isCollaborator: false
        };
      }
      
      // Se for 'business', vamos considerar como perfil principal
      if (profileId === 'business') {
        return {
          userId: auth.currentUser.uid,
          subpath: 'business',
          isCollaborator: false
        };
      }
      
      // Para o perfil personalizado, usar o subcaminho com ID
      return {
        userId: auth.currentUser.uid,
        subpath: profileId,
        isCollaborator: false
      };
    }
  } catch (error) {
    console.error('Erro ao obter caminho da conta:', error);
    
    // Fallback para 'business' em caso de erro
    return {
      userId: auth.currentUser.uid,
      subpath: 'business',
      isCollaborator: false
    };
  }
};

/**
 * Carrega os períodos disponíveis para o usuário atual
 * @returns {Promise<Array>} - Lista de períodos disponíveis
 */
export const loadAvailablePeriods = async () => {
  try {
    if (!auth.currentUser) return [];
    
    // Obter caminho da conta (considerando colaboradores)
    const { userId, subpath } = await getAccountPath();
    
    console.log(`Carregando períodos para usuário ${userId}, subpath ${subpath}`);
    
    // Usar a subcoleção correta com base no subcaminho da conta
    const periodsQuery = query(
      collection(db, "ofxFiles", userId, subpath)
    );
    
    const periodsSnapshot = await getDocs(periodsQuery);
    const uniquePeriods = [];
    const periodsSet = new Set();
    
    periodsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.period && !periodsSet.has(data.period)) {
        periodsSet.add(data.period);
        uniquePeriods.push({
          value: data.period,
          label: data.periodLabel || data.period
        });
      }
    });
    
    // Ordenar os períodos (mais recente primeiro)
    uniquePeriods.sort((a, b) => b.value.localeCompare(a.value));
    
    console.log(`Encontrados ${uniquePeriods.length} períodos.`);
    
    return uniquePeriods;
  } catch (error) {
    console.error("Erro ao carregar períodos:", error);
    throw new Error("Não foi possível carregar os períodos disponíveis.");
  }
};

/**
 * Carrega as categorias do usuário atual
 * @returns {Promise<Object>} - Categorias do usuário
 */
export const loadUserCategories = async () => {
  try {
    if (!auth.currentUser) return {};
    
    // Obter caminho da conta (considerando colaboradores)
    const { userId, subpath } = await getAccountPath();
    const isPersonal = subpath === 'personal';
    
    console.log(`Carregando categorias para usuário ${userId}, subpath ${subpath}`);
    
    // Caminho para o documento de categorias
    let categoriesDocRef;
    
    if (isPersonal) {
      // Para conta pessoal, usar a subcoleção "personal/categories"
      categoriesDocRef = doc(db, "userCategories", userId, "personal", "categories");
    } else {
      // Para perfil principal, verificar diretamente o caminho 'business/categories'
      if (subpath === 'business') {
        // Para o perfil principal/padrão, verificar primeiro no novo caminho estruturado
        categoriesDocRef = doc(db, "userCategories", userId, "business", "categories");
        
        // Verificar se o documento existe
        const docSnapshot = await getDoc(categoriesDocRef);
        
        // Se não existir, tentar o caminho antigo (retrocompatibilidade)
        if (!docSnapshot.exists()) {
          console.log(`Documento de categorias não encontrado para perfil business, tentando caminho antigo...`);
          categoriesDocRef = doc(db, "userCategories", userId);
        }
      } else {
        // Para outros perfis empresariais, usar o ID do perfil
        categoriesDocRef = doc(db, "userCategories", userId, subpath, "categories");
      }
    }
    
    const userCategoriesDoc = await getDoc(categoriesDocRef);
    
    if (userCategoriesDoc.exists()) {
      const categoriesData = userCategoriesDoc.data();
      const businessModel = categoriesData.businessModel || 
                          (isPersonal ? "pessoal" : "restaurante");
      
      // Se não houver categorias selecionadas, usar uma estrutura padrão baseada no modelo de negócio
      if (!categoriesData.categories || Object.keys(categoriesData.categories).length === 0) {
        if (businessModel === "pessoal" || isPersonal) {
          // Fornecer todas as categorias do modelo pessoal
          return {
            "💸 Receitas": {
              items: ["Salário", "Freelance", "Reembolsos", "Depósitos diversos"]
            },
            "🧾 Despesas Fixas": {
              items: ["Aluguel/Financiamento", "Condomínio", "Energia", "Água", "Internet/Telefone", 
                      "Academia", "Assinaturas (Netflix, Spotify, etc.)"]
            },
            "🍞 Despesas Variáveis": {
              items: ["Supermercado", "Farmácia", "Transporte (Uber, gasolina, transporte público)",
                      "Lanches/Restaurantes", "Lazer (cinema, shows, viagens)", "Compras (roupas, eletrônicos, etc.)"]
            },
            "💳 Pagamentos e Transferências": {
              items: ["Fatura do Cartão", "Transferências Pix/TED", "Pagamentos de empréstimos", "Tarifa bancária"]
            }
          };
        }
      }
      
      // Process categories into a more usable format for the TransactionItem component
      if (categoriesData.categories) {
        // Extrair categorias selecionadas organizadas por grupo
        const categoriesByGroup = {};
        
        Object.keys(categoriesData.categories).forEach(path => {
          if (categoriesData.categories[path]) {
            const parts = path.split('.');
            if (parts.length === 2) {
              const group = parts[0];
              const item = parts[1];
              
              if (!categoriesByGroup[group]) {
                categoriesByGroup[group] = {
                  items: []
                };
              }
              
              // Verificar se o item já existe no array (evitar duplicatas)
              if (!categoriesByGroup[group].items.includes(item)) {
                categoriesByGroup[group].items.push(item);
              }
            }
          }
        });
        
        return categoriesByGroup;
      }
    }
    
    return {};
  } catch (error) {
    console.error("Erro ao carregar categorias:", error);
    throw new Error("Não foi possível carregar suas categorias.");
  }
};

/**
 * Carrega os mapeamentos de categorias do usuário atual
 * @returns {Promise<Object>} - Mapeamentos de categorias
 */
export const loadCategoryMappings = async () => {
  try {
    if (!auth.currentUser) return {};
    
    // Obter caminho da conta (considerando colaboradores)
    const { userId, subpath } = await getAccountPath();
    const isPersonal = subpath === 'personal';
    
    console.log(`Carregando mapeamentos para usuário ${userId}, subpath ${subpath}`);
    
    // Caminho para o documento de mapeamentos
    let mappingsDocRef;
    
    if (isPersonal) {
      // Para conta pessoal, usar a subcoleção "personal/mappings"
      mappingsDocRef = doc(db, "categoryMappings", userId, "personal", "mappings");
    } else {
      // Para perfil principal, verificar diretamente o caminho 'business/mappings'
      if (subpath === 'business') {
        // Para o perfil principal/padrão, verificar primeiro no novo caminho estruturado
        mappingsDocRef = doc(db, "categoryMappings", userId, "business", "mappings");
        
        // Verificar se o documento existe
        const docSnapshot = await getDoc(mappingsDocRef);
        
        // Se não existir, tentar o caminho antigo (retrocompatibilidade)
        if (!docSnapshot.exists()) {
          console.log(`Documento de mapeamentos não encontrado para perfil business, tentando caminho antigo...`);
          mappingsDocRef = doc(db, "categoryMappings", userId);
        }
      } else {
        // Para outros perfis empresariais, usar o ID do perfil
        mappingsDocRef = doc(db, "categoryMappings", userId, subpath, "mappings");
      }
    }
    
    const mappingsDoc = await getDoc(mappingsDocRef);
    
    if (mappingsDoc.exists()) {
      // Se for um documento legado sem a chave 'mappings'
      if (!mappingsDoc.data().mappings) {
        return mappingsDoc.data() || {};
      }
      
      // Formato padrão com chave 'mappings'
      if (mappingsDoc.data().mappings) {
        return mappingsDoc.data().mappings;
      }
    }
    
    return {};
  } catch (error) {
    console.error("Erro ao carregar mapeamentos de categorias:", error);
    throw new Error("Não foi possível carregar os mapeamentos de categorias.");
  }
};

/**
 * Carrega transações para um período
 * @param {string} period - Período selecionado
 * @returns {Promise<Object>} - Objeto com transações e informações relacionadas
 */
export const loadTransactionsForPeriod = async (period) => {
  try {
    if (!auth.currentUser || !period) {
      return { transactions: [], autoMappedToSave: [] };
    }
    
    // Obter caminho da conta (considerando colaboradores)
    const { userId, subpath, isCollaborator, collaboratorInfo } = await getAccountPath();
    
    console.log(`Carregando transações para usuário ${userId}, subpath ${subpath}, período ${period}`);
    if (isCollaborator) {
      console.log(`Acesso colaborador: ${auth.currentUser.uid} acessando dados de ${userId}`);
    }
    
    // Buscar arquivos OFX do período selecionado usando a subcoleção correta
    const ofxFilesQuery = query(
      collection(db, "ofxFiles", userId, subpath),
      where("period", "==", period)
    );
    
    const ofxFilesSnapshot = await getDocs(ofxFilesQuery);
    console.log(`Encontrados ${ofxFilesSnapshot.docs.length} arquivos OFX`);
    
    const transactions = [];
    const autoMappedToSave = [];
    
    // Processar cada arquivo OFX
    for (const fileDoc of ofxFilesSnapshot.docs) {
      const fileData = fileDoc.data();
      
      if (fileData.filePath) {
        try {
          console.log(`Processando arquivo: ${fileData.fileName}`);
          const storageRef = ref(storage, fileData.filePath);
          const blob = await getBlob(storageRef);
          const fileContent = await blob.text();
          const parseResult = parseOFXContent(fileContent);
          
          console.log(`Extraídas ${parseResult.transactions.length} transações do arquivo`);
          
          // Adicionar metadados a cada transação
          parseResult.transactions.forEach(transaction => {
            transactions.push({
              ...transaction,
              fileId: fileDoc.id,
              fileName: fileData.fileName,
              period: fileData.period,
              periodLabel: fileData.periodLabel,
              accountSubpath: subpath, // Adicionar o subcaminho como metadado
              ownerUid: userId, // Adicionar o UID do proprietário
              isCollaboratorView: isCollaborator // Indicar se é visualização de colaborador
            });
            
            // Se a transação foi auto-mapeada durante parsing mas ainda não salva
            if (transaction.autoMapped && transaction.category && transaction.categoryPath) {
              autoMappedToSave.push({
                ...transaction,
                id: transaction.id,
                description: transaction.description,
                groupName: transaction.categoryPath.split('.')[0],
                accountSubpath: subpath,
                ownerUid: userId // Adicionar o UID do proprietário
              });
            }
          });
        } catch (error) {
          console.error(`Erro ao processar arquivo OFX ${fileData.fileName}:`, error);
        }
      }
    }
    
    // Ordenar transações por data (mais recente primeiro)
    transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    console.log(`Total de ${transactions.length} transações carregadas`);
    
    return { transactions, autoMappedToSave };
  } catch (error) {
    console.error("Erro ao carregar transações:", error);
    throw new Error("Não foi possível carregar as transações para o período selecionado.");
  }
};

/**
 * Salva mapeamentos de categorias no Firestore
 * @param {Object} updatedMappings - Mapeamentos atualizados para salvar
 * @returns {Promise<void>}
 */
export const saveCategoryMappings = async (updatedMappings) => {
  try {
    if (!auth.currentUser) {
      throw new Error("Usuário não autenticado");
    }
    
    // Obter caminho da conta (considerando colaboradores)
    const { userId, subpath, isCollaborator } = await getAccountPath();
    
    const isPersonal = subpath === 'personal';
    
    // Caminho para o documento de mapeamentos
    let categoryMappingsRef;
    
    if (isPersonal) {
      // Para conta pessoal, usar a subcoleção "personal/mappings"
      categoryMappingsRef = doc(db, "categoryMappings", userId, "personal", "mappings");
    } else {
      // Para perfil principal, usar o caminho 'business/mappings'
      if (subpath === 'business') {
        // Para o perfil principal, usar o caminho estruturado
        categoryMappingsRef = doc(db, "categoryMappings", userId, "business", "mappings");
        
        // Verificar se o documento existe
        const docSnapshot = await getDoc(categoryMappingsRef);
        
        // Se não existir, podemos precisar também atualizar o caminho legado
        if (!docSnapshot.exists()) {
          // Salvar no caminho legado para retrocompatibilidade
          const legacyMappingsRef = doc(db, "categoryMappings", userId);
          await setDoc(legacyMappingsRef, {
            mappings: updatedMappings,
            updatedAt: serverTimestamp()
          });
        }
      } else {
        // Para outros perfis empresariais, usar o ID do perfil
        categoryMappingsRef = doc(db, "categoryMappings", userId, subpath, "mappings");
      }
    }
    
    // Verificar se o documento já existe
    const docSnapshot = await getDoc(categoryMappingsRef);
    
    if (docSnapshot.exists()) {
      // Atualizar o documento existente
      await updateDoc(categoryMappingsRef, {
        mappings: updatedMappings,
        updatedAt: serverTimestamp(),
        // Adicionar informação do colaborador se aplicável
        ...(isCollaborator ? {
          lastEditedBy: auth.currentUser.uid,
          lastEditedByName: auth.currentUser.displayName || "Colaborador",
          lastEditedAt: serverTimestamp()
        } : {})
      });
    } else {
      // Criar um novo documento
      await setDoc(categoryMappingsRef, {
        userId: userId,
        mappings: updatedMappings,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        // Adicionar informação do colaborador se aplicável
        ...(isCollaborator ? {
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || "Colaborador"
        } : {})
      });
    }
  } catch (error) {
    console.error("Erro ao salvar mapeamentos:", error);
    throw new Error(`Não foi possível salvar os mapeamentos: ${error.message}`);
  }
};